// src/pages/KarigarManagement/Job/components/JobImagesForm.jsx

import React from "react";
import { Button } from "@/components/ui-v2/button";
import { Badge } from "@/components/ui-v2/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import {
  ShoppingBag,
  Image as ImageIcon,
  Camera,
  AlertTriangle,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ImageUpload from "@/components/common/ImageUpload";
import ProductImageCard from "./ProductImageCard";
import ImagePreviewModal from "./ImagePreviewModal";

/**
 * Step 4: Images Form
 * Handles product images, reference images, and item images
 */
const JobImagesForm = ({
  // Product images
  productImageUrls = [],
  removedProductImageIndexes = new Set(),
  activeProductImages = [],
  onRemoveProductImage,
  onRestoreAllProductImages,
  onRemoveAllProductImages,

  // Existing images (edit mode)
  existingReferenceImages = [],
  removedExistingImageIndexes = new Set(),
  activeExistingImages = [],
  onRemoveExistingImage,
  onRestoreAllExistingImages,

  // New uploaded images
  referenceImages = [],
  onReferenceImagesChange,
  itemImages = [],
  onItemImagesChange,

  // Preview
  previewImage,
  onPreviewImage,
  onClosePreview,

  // Flags
  needsItemDetails = false,
  isEditMode = false,
}) => {
  const hasProductImages = productImageUrls.length > 0;
  const hasExistingImages = existingReferenceImages.length > 0;
  const hasRemovedProductImages = removedProductImageIndexes.size > 0;
  const hasRemovedExistingImages = removedExistingImageIndexes.size > 0;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold">
          {isEditMode ? "Manage Images" : "Upload Images"}
        </h2>
        <p className="text-muted-foreground">
          {isEditMode
            ? "Add, remove, or manage images for this job"
            : "Add reference images and item photos for this job"}
        </p>
      </div>

      {/* Product Images (from product selection) */}
      {hasProductImages && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-purple-600" />
                  Product Images
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs",
                      hasRemovedProductImages && "bg-orange-100 text-orange-700"
                    )}
                  >
                    {activeProductImages.length}/{productImageUrls.length} selected
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Click on images to remove or restore them. Removed images won't
                  be included.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {hasRemovedProductImages && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRestoreAllProductImages}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore All
                  </Button>
                )}
                {removedProductImageIndexes.size < productImageUrls.length && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRemoveAllProductImages}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove All
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {productImageUrls.map((url, index) => (
                <ProductImageCard
                  key={`product-${index}`}
                  url={url}
                  index={index}
                  onRemove={onRemoveProductImage}
                  onPreview={onPreviewImage}
                  isRemoved={removedProductImageIndexes.has(index)}
                  label="Product"
                />
              ))}
            </div>

            {hasRemovedProductImages && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                <p className="text-sm text-orange-700">
                  {removedProductImageIndexes.size} image(s) removed.{" "}
                  {activeProductImages.length} image(s) will be included as
                  reference.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Existing Images (edit mode) */}
      {isEditMode && hasExistingImages && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-blue-600" />
                  Existing Images
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs",
                      hasRemovedExistingImages && "bg-orange-100 text-orange-700"
                    )}
                  >
                    {activeExistingImages.length}/{existingReferenceImages.length}{" "}
                    selected
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Previously uploaded images. Click to remove or restore.
                </CardDescription>
              </div>
              {hasRemovedExistingImages && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRestoreAllExistingImages}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Restore All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {existingReferenceImages.map((url, index) => (
                <ProductImageCard
                  key={`existing-${index}`}
                  url={url}
                  index={index}
                  onRemove={onRemoveExistingImage}
                  onPreview={onPreviewImage}
                  isRemoved={removedExistingImageIndexes.has(index)}
                  label="Existing"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reference Images Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              {hasProductImages || hasExistingImages
                ? "Additional Reference Images"
                : "Reference Images"}
            </CardTitle>
            <CardDescription>
              Upload design references, inspirations, or sample images (max 10)
              {(activeProductImages.length > 0 || activeExistingImages.length > 0) && (
                <span className="text-purple-600">
                  {" "}
                  • {activeProductImages.length + activeExistingImages.length} image(s)
                  already included
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ImageUpload
              files={referenceImages}
              onFilesChange={onReferenceImagesChange}
              maxFiles={10}
              maxSizeMB={5}
              label=""
              description="Design references, sample images, or sketches"
              allowCamera={true}
              gridCols={3}
            />
          </CardContent>
        </Card>

        {/* Item Images (for repair/alteration) or Additional Images */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {needsItemDetails ? "Item Images" : "Additional Images"}
            </CardTitle>
            <CardDescription>
              {needsItemDetails
                ? "Upload photos of the customer's item for repair/alteration (max 5)"
                : "Upload any other relevant images (max 5)"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ImageUpload
              files={itemImages}
              onFilesChange={onItemImagesChange}
              maxFiles={5}
              maxSizeMB={5}
              label=""
              description={
                needsItemDetails
                  ? "Photos of the item to be repaired or altered"
                  : "Any additional images for this job"
              }
              allowCamera={true}
              gridCols={3}
            />
          </CardContent>
        </Card>
      </div>

      {/* Image Summary */}
      {(activeProductImages.length > 0 ||
        activeExistingImages.length > 0 ||
        referenceImages.length > 0 ||
        itemImages.length > 0) && (
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <ImageIcon className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Total Images for this Job</p>
                <p className="text-sm text-muted-foreground">
                  {activeProductImages.length > 0 && (
                    <span className="text-purple-600">
                      {activeProductImages.length} from product
                      {hasRemovedProductImages && (
                        <span className="text-orange-500">
                          {" "}
                          ({removedProductImageIndexes.size} removed)
                        </span>
                      )}
                    </span>
                  )}
                  {activeProductImages.length > 0 &&
                    (activeExistingImages.length > 0 || referenceImages.length > 0) &&
                    " + "}
                  {activeExistingImages.length > 0 && (
                    <span className="text-blue-600">
                      {activeExistingImages.length} existing
                    </span>
                  )}
                  {activeExistingImages.length > 0 && referenceImages.length > 0 && " + "}
                  {referenceImages.length > 0 && (
                    <span>{referenceImages.length} new upload(s)</span>
                  )}
                  {(activeProductImages.length > 0 ||
                    activeExistingImages.length > 0 ||
                    referenceImages.length > 0) &&
                    itemImages.length > 0 &&
                    " + "}
                  {itemImages.length > 0 && (
                    <span>{itemImages.length} item image(s)</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={onClosePreview}
        imageUrl={previewImage}
        title="Image Preview"
      />
    </div>
  );
};

export default JobImagesForm;
